import {
  createVersionedSessionDraftStore,
  type VersionedSessionDraftStrictMutationOptions,
} from "@/app/booking/core/versionedSessionDraftStore";

import {
  isAppointmentWaitlistDraftTerminallyBlocked,
  validateAppointmentWaitlistDraft,
  validateAppointmentWaitlistDraftForAction,
  type AppointmentWaitlistActionAuthority,
  type AppointmentWaitlistDraftV3,
} from "./appointmentWaitlistDraft";
import { haveExactAppointmentWaitlistConfigurationAuthority } from "./appointmentWaitlistConfiguration";
import { isAppointmentWaitlistConflictPublicationCurrent } from "./appointmentWaitlistConfiguration";
import { haveExactAppointmentTimeProfessionalMutationAuthority } from "@/app/booking/scheduling/appointmentTimeProfessionalMutation";
import { haveExactAppointmentWaitlistPreSlotQuoteAuthority } from "./appointmentWaitlistPreSlotQuote";
import { migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage } from "./retiredGroupAppointmentWaitlistStorageMigration";
import { APPOINTMENT_WAITLIST_DRAFT_STORAGE_MAX_SERIALIZED_CHARACTERS } from "./appointmentWaitlistStorageLimits";

export const APPOINTMENT_WAITLIST_DRAFT_STORAGE_KEY =
  "volera:appointment-waitlist-drafts:v3";
export const LEGACY_APPOINTMENT_WAITLIST_DRAFT_V2_STORAGE_KEY =
  "volera:appointment-waitlist-drafts:v2";
export const LEGACY_APPOINTMENT_WAITLIST_DRAFT_V1_STORAGE_KEY =
  "volera:appointment-waitlist-drafts:v1";

const appointmentWaitlistDraftStore =
  createVersionedSessionDraftStore<AppointmentWaitlistDraftV3>({
    storageKey: APPOINTMENT_WAITLIST_DRAFT_STORAGE_KEY,
    storeKind: "appointment-waitlist-drafts",
    maxEntries: 8,
    maxAgeMs: 6 * 60 * 60 * 1_000,
    maxSerializedCharacters:
      APPOINTMENT_WAITLIST_DRAFT_STORAGE_MAX_SERIALIZED_CHARACTERS,
    beforeAccess:
      migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage,
    validate: validateAppointmentWaitlistDraft,
  });

function hasSameWaitlistLineage(
  left: AppointmentWaitlistDraftV3,
  right: AppointmentWaitlistDraftV3,
) {
  return (
    left.draftId === right.draftId &&
    left.sourceAppointmentDraftSchemaVersion ===
      right.sourceAppointmentDraftSchemaVersion &&
    JSON.stringify(left.appointmentDraft) ===
      JSON.stringify(right.appointmentDraft) &&
    JSON.stringify(left.staffChoiceEvidence) ===
      JSON.stringify(right.staffChoiceEvidence) &&
    JSON.stringify(left.professionalSelectionEvidence) ===
      JSON.stringify(right.professionalSelectionEvidence) &&
    haveExactAppointmentWaitlistPreSlotQuoteAuthority(
      left.preSlotQuoteSnapshot,
      right.preSlotQuoteSnapshot,
    ) &&
    (left.timeProfessionalMutationEvidence === null
      ? right.timeProfessionalMutationEvidence === null
      : right.timeProfessionalMutationEvidence !== null &&
        haveExactAppointmentTimeProfessionalMutationAuthority(
          left.timeProfessionalMutationEvidence,
          right.timeProfessionalMutationEvidence,
        )) &&
    left.returnDateId === right.returnDateId &&
    haveExactAppointmentWaitlistConfigurationAuthority(
      left.configurationPublication,
      right.configurationPublication,
    )
  );
}

function hasSameTemporalAuthorityInstances(
  left: AppointmentWaitlistDraftV3,
  right: AppointmentWaitlistDraftV3,
) {
  return (
    JSON.stringify(left.configurationPublication) ===
      JSON.stringify(right.configurationPublication) &&
    JSON.stringify(left.timeProfessionalMutationEvidence) ===
      JSON.stringify(right.timeProfessionalMutationEvidence)
  );
}

function isLegalWaitlistTransition(
  current: AppointmentWaitlistDraftV3,
  next: AppointmentWaitlistDraftV3,
  evaluatedAtEpochMs: number,
) {
  if (
    !hasSameWaitlistLineage(current, next) ||
    !hasSameTemporalAuthorityInstances(current, next) ||
    (current.customerAccountId !== null &&
      current.customerAccountId !== next.customerAccountId)
  ) {
    return false;
  }

  const samePreferences =
    JSON.stringify(current.preferences) === JSON.stringify(next.preferences);
  const sameConflict =
    JSON.stringify(current.conflictPublication) ===
    JSON.stringify(next.conflictPublication);
  const sameAnswer = current.firstVisitAnswer === next.firstVisitAnswer;
  const sameConfirmation =
    JSON.stringify(current.confirmation) === JSON.stringify(next.confirmation);
  const preferencesChanged =
    !samePreferences;
  const customerBound =
    current.customerAccountId === null &&
    next.customerAccountId !== null &&
    current.stage === next.stage &&
    samePreferences &&
    sameConflict &&
    sameAnswer &&
    sameConfirmation;
  const noCoreChange =
    current.customerAccountId === next.customerAccountId &&
    current.stage === next.stage &&
    samePreferences &&
    sameConflict &&
    sameAnswer &&
    sameConfirmation;
  const preferencesEdit =
    current.customerAccountId === next.customerAccountId &&
    current.stage === "preferences" &&
    next.stage === "preferences" &&
    preferencesChanged &&
    next.conflictPublication === null &&
    next.firstVisitAnswer === null &&
    next.confirmation === null;
  const conflictPublicationEdit =
    current.customerAccountId === next.customerAccountId &&
    current.stage === "preferences" &&
    next.stage === "preferences" &&
    samePreferences &&
    !sameConflict &&
    next.conflictPublication !== null &&
    isAppointmentWaitlistConflictPublicationCurrent({
      publication: next.conflictPublication,
      configuration: next.configurationPublication,
      preferences: next.preferences,
      expectedProviderIdentity: next.configurationPublication.providerIdentity,
      evaluatedAtEpochMs,
    }) &&
    sameAnswer &&
    sameConfirmation;
  const answerEdit =
    current.customerAccountId === next.customerAccountId &&
    current.stage === "first-visit" &&
    next.stage === "first-visit" &&
    samePreferences &&
    sameConflict &&
    !sameAnswer &&
    sameConfirmation;
  const advanceFromPreferences =
    current.customerAccountId !== null &&
    current.customerAccountId === next.customerAccountId &&
    current.stage === "preferences" &&
    next.stage ===
      (current.configurationPublication.firstVisit.kind === "required"
        ? "first-visit"
        : "confirmation") &&
    samePreferences &&
    sameConflict &&
    current.firstVisitAnswer === null &&
    next.firstVisitAnswer === null &&
    current.confirmation === null &&
    (next.stage === "first-visit"
      ? next.confirmation === null
      : next.confirmation?.note === null &&
        JSON.stringify(next.confirmation.paymentPresentation) ===
          JSON.stringify(next.configurationPublication.paymentPresentation));
  const advanceFromFirstVisit =
    current.customerAccountId !== null &&
    current.customerAccountId === next.customerAccountId &&
    current.stage === "first-visit" &&
    next.stage === "confirmation" &&
    samePreferences &&
    sameConflict &&
    current.firstVisitAnswer !== null &&
    sameAnswer &&
    current.confirmation === null &&
    next.confirmation?.note === null &&
    JSON.stringify(next.confirmation.paymentPresentation) ===
      JSON.stringify(next.configurationPublication.paymentPresentation);
  const retreat =
    current.customerAccountId === next.customerAccountId &&
    ((current.stage === "first-visit" && next.stage === "preferences") ||
      (current.stage === "confirmation" &&
        next.stage ===
          (current.configurationPublication.firstVisit.kind === "required"
            ? "first-visit"
            : "preferences"))) &&
    samePreferences &&
    sameConflict &&
    next.confirmation === null &&
    (next.stage === "first-visit"
      ? sameAnswer
      : next.firstVisitAnswer === null);
  const confirmationNoteEdit =
    current.customerAccountId === next.customerAccountId &&
    current.stage === "confirmation" &&
    next.stage === "confirmation" &&
    samePreferences &&
    sameConflict &&
    sameAnswer &&
    current.confirmation !== null &&
    next.confirmation !== null &&
    JSON.stringify(current.confirmation.paymentPresentation) ===
      JSON.stringify(next.confirmation.paymentPresentation);
  const legalCoreDelta =
    noCoreChange ||
    customerBound ||
    preferencesEdit ||
    conflictPublicationEdit ||
    answerEdit ||
    advanceFromPreferences ||
    advanceFromFirstVisit ||
    retreat ||
    confirmationNoteEdit;
  if (!legalCoreDelta) return false;

  if (current.terminalFailure) {
    if (next.terminalFailure) {
      const sameMarker =
        JSON.stringify(current.terminalFailure) ===
        JSON.stringify(next.terminalFailure);
      return (
        (sameMarker && legalCoreDelta) ||
        (!isAppointmentWaitlistDraftTerminallyBlocked(current) &&
          isAppointmentWaitlistDraftTerminallyBlocked(next) &&
          legalCoreDelta)
      );
    }
    return false;
  }

  if (next.terminalFailure) {
    return (
      isAppointmentWaitlistDraftTerminallyBlocked(next) &&
      current.customerAccountId !== null &&
      current.customerAccountId === next.customerAccountId &&
      current.stage === "confirmation" &&
      next.stage === "confirmation" &&
      samePreferences &&
      sameConfirmation &&
      sameAnswer &&
      sameConflict
    );
  }

  return legalCoreDelta;
}

function storeValidatedAppointmentWaitlistDraft(
  value: unknown,
  requirePersistence: boolean,
  evaluatedAtEpochMs?: number,
) {
  let evaluatedEpoch: number;
  try {
    evaluatedEpoch = evaluatedAtEpochMs ?? Date.now();
  } catch {
    return null;
  }
  if (!Number.isSafeInteger(evaluatedEpoch) || evaluatedEpoch < 0) return null;
  const validation = validateAppointmentWaitlistDraft(value);

  if (!validation.valid) {
    return null;
  }

  const draft = validation.draft;
  const existing = appointmentWaitlistDraftStore.readDraft(
    draft.draftId,
    evaluatedEpoch,
  );

  if (existing && !isLegalWaitlistTransition(existing, draft, evaluatedEpoch)) {
    return null;
  }

  return requirePersistence ||
    draft.terminalFailure ||
    existing?.terminalFailure
    ? appointmentWaitlistDraftStore.storeDraftPersisted(draft, evaluatedEpoch)
    : appointmentWaitlistDraftStore.storeDraft(draft, evaluatedEpoch);
}

export function storeAppointmentWaitlistDraft(
  value: unknown,
  evaluatedAtEpochMs?: number,
) {
  return storeValidatedAppointmentWaitlistDraft(
    value,
    false,
    evaluatedAtEpochMs,
  );
}

export function storeAppointmentWaitlistDraftPersisted(
  value: unknown,
  evaluatedAtEpochMs?: number,
) {
  return storeValidatedAppointmentWaitlistDraft(
    value,
    true,
    evaluatedAtEpochMs,
  );
}

/**
 * Dedicated exact-CAS authority rotation. Generic writes cannot rotate config
 * or Time instances. A fresh conflict must cover the rotated configuration;
 * bookable recovery returns to Preferences with descendants already cleared.
 */
export function refreshStoredAppointmentWaitlistDraftAuthority(input: {
  sourceDraft: unknown;
  refreshedDraft: unknown;
  actionAuthority: AppointmentWaitlistActionAuthority;
  evaluatedAtEpochMs?: number;
}) {
  let evaluatedAtEpochMs: number;
  try {
    evaluatedAtEpochMs = input.evaluatedAtEpochMs ?? Date.now();
  } catch {
    return null;
  }
  if (!Number.isSafeInteger(evaluatedAtEpochMs) || evaluatedAtEpochMs < 0) {
    return null;
  }
  const current = validateAppointmentWaitlistDraft(input.sourceDraft);
  const next = validateAppointmentWaitlistDraft(input.refreshedDraft);
  const action = next.valid
    ? validateAppointmentWaitlistDraftForAction(
        next.draft,
        { ...input.actionAuthority, evaluatedAtEpochMs },
      )
    : null;
  if (
    !current.valid ||
    !next.valid ||
    !action?.valid ||
    !hasSameWaitlistLineage(current.draft, next.draft) ||
    current.draft.customerAccountId !== next.draft.customerAccountId ||
    JSON.stringify(current.draft.preferences) !==
      JSON.stringify(next.draft.preferences) ||
    JSON.stringify(current.draft.terminalFailure) !==
      JSON.stringify(next.draft.terminalFailure) ||
    !next.draft.conflictPublication ||
    !isAppointmentWaitlistConflictPublicationCurrent({
      publication: next.draft.conflictPublication,
      configuration: next.draft.configurationPublication,
      preferences: next.draft.preferences,
      expectedProviderIdentity:
        input.actionAuthority.expectedConfigurationProviderIdentity,
      evaluatedAtEpochMs,
    }) ||
    (next.draft.conflictPublication.result.kind === "none" &&
      (current.draft.stage !== next.draft.stage ||
        current.draft.firstVisitAnswer !== next.draft.firstVisitAnswer ||
        JSON.stringify(current.draft.confirmation) !==
          JSON.stringify(next.draft.confirmation))) ||
    (next.draft.conflictPublication?.result.kind === "bookable" &&
      (next.draft.stage !== "preferences" ||
        next.draft.firstVisitAnswer !== null ||
        next.draft.confirmation !== null))
  ) {
    return null;
  }
  const existing = appointmentWaitlistDraftStore.readDraft(
    current.draft.draftId,
    evaluatedAtEpochMs,
  );
  if (!existing || JSON.stringify(existing) !== JSON.stringify(current.draft))
    return null;
  return appointmentWaitlistDraftStore.storeDraftPersisted(
    next.draft,
    evaluatedAtEpochMs,
  );
}

export function readAppointmentWaitlistDraft(
  draftId: string,
  evaluatedAtEpochMs?: number,
) {
  return appointmentWaitlistDraftStore.readDraft(draftId, evaluatedAtEpochMs);
}

export function inspectAppointmentWaitlistDraftsPersisted() {
  return appointmentWaitlistDraftStore.inspectPersistedDrafts();
}

export function inspectAppointmentWaitlistDraftPersisted(
  draftId: string,
  evaluatedAtEpochMs?: number,
) {
  return appointmentWaitlistDraftStore.inspectPersistedDraft(
    draftId,
    evaluatedAtEpochMs,
  );
}

export function createAppointmentWaitlistDraftStrictPersisted(
  value: unknown,
  evaluatedAtEpochMsOrOptions?:
    | number
    | VersionedSessionDraftStrictMutationOptions,
) {
  return appointmentWaitlistDraftStore.createDraftStrictPersisted(
    value,
    evaluatedAtEpochMsOrOptions,
  );
}

export function replaceAppointmentWaitlistDraftStrictPersisted(
  expectedValue: unknown,
  nextValue: unknown,
  options: VersionedSessionDraftStrictMutationOptions = {},
) {
  return appointmentWaitlistDraftStore.replaceDraftStrictPersisted(
    expectedValue,
    nextValue,
    options,
  );
}

export function removeAppointmentWaitlistDraftStrictPersisted(
  value: unknown,
  evaluatedAtEpochMsOrOptions?:
    | number
    | VersionedSessionDraftStrictMutationOptions,
) {
  return appointmentWaitlistDraftStore.removeDraftStrictPersisted(
    value,
    evaluatedAtEpochMsOrOptions,
  );
}

export function removeAppointmentWaitlistDraft(draftId: string) {
  return appointmentWaitlistDraftStore.removeDraft(draftId);
}

export function ensureAppointmentWaitlistDraftRemoved(
  draftId: string,
  evaluatedAtEpochMs?: number,
) {
  return appointmentWaitlistDraftStore.ensureDraftRemoved(
    draftId,
    evaluatedAtEpochMs,
  );
}

export function clearAppointmentWaitlistDrafts() {
  const currentCleared = appointmentWaitlistDraftStore.clearDrafts();
  let legacyCleared = false;

  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(
        LEGACY_APPOINTMENT_WAITLIST_DRAFT_V1_STORAGE_KEY,
      );
      window.sessionStorage.removeItem(
        LEGACY_APPOINTMENT_WAITLIST_DRAFT_V2_STORAGE_KEY,
      );
      legacyCleared =
        window.sessionStorage.getItem(
          LEGACY_APPOINTMENT_WAITLIST_DRAFT_V1_STORAGE_KEY,
        ) === null &&
        window.sessionStorage.getItem(
          LEGACY_APPOINTMENT_WAITLIST_DRAFT_V2_STORAGE_KEY,
        ) === null;
    } catch {
      legacyCleared = false;
    }
  }

  return currentCleared && legacyCleared;
}
